import React from "react";
import { getExchangeValues } from "@/components/Sidebar/getExchangeValues";
import {
  useProfile,
  usePositionsData,
  useHasTransactions,
  useIntegrations,
  formatAmount,
  getCurrencySymbol,
} from "@moonscape/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyWidget } from "@/components/ui/empty-widget";
import { ChartPieIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { CoinMark, DHead, Tag } from "@/components/ms";
import { FigLink, HeadLink } from "./chrome";

export const TopHoldingsWidget = () => {
  const { profile } = useProfile();
  const { data: positionsData, isLoading } = usePositionsData();
  const { data: hasTransactions, isLoading: isLoadingTxns } = useHasTransactions();
  const { data: integrationsData } = useIntegrations();

  // Get base currency
  const baseCurrency = profile?.baseCurrency || "USD";

  // Extract balances from positions response
  const positions = positionsData?.balances || [];
  const positionsArray = Array.isArray(positions) ? positions : [];

  // Calculate currentValue for each position (price * amount)
  const positionsWithValue = positionsArray.map(p => ({
    ...p,
    currentValue: (p.price || 0) * (p.amount || 0),
    quantity: p.amount || 0,
  }));

  // Calculate total portfolio value
  const totalValue = positionsWithValue.reduce((sum, p) => sum + (p.currentValue || 0), 0);

  // Sort by value and take top 5
  const topHoldings = positionsWithValue
    .sort((a, b) => (b.currentValue || 0) - (a.currentValue || 0))
    .slice(0, 5);

  // Format currency
  const formatCurrency = (amount: number) => {
    const symbol = getCurrencySymbol(baseCurrency);
    return formatAmount(amount, symbol, true);
  };

  // Check if any sync is in progress
  const hasSyncInProgress = integrationsData?.items?.some(
    (integration) =>
      integration.sync_state === 'starting' ||
      integration.sync_state === 'in_progress'
  ) || false;

  // Hide widget if user has transactions but no balances (avoid misleading CTA)
  // BUT don't hide if sync is in progress (balances may be loading)
  if (!isLoading && !isLoadingTxns && topHoldings.length === 0 && hasTransactions && !hasSyncInProgress) {
    return null;
  }

  // Loading state
  if (isLoading || isLoadingTxns) {
    return (
      <section className="flex flex-col">
        <DHead label="Holdings" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 border-t border-ms-line py-[11px] first:border-t-0">
            <Skeleton circle width={30} height={30} />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </section>
    );
  }

  // Empty state — no fake data, just the one thing to do next
  if (topHoldings.length === 0) {
    return (
      <section className="flex flex-col">
        <DHead label="Holdings" />
        {!profile?.taxJurisdiction ? (
          <EmptyWidget
            icon={<ExclamationTriangleIcon className="h-8 w-8 text-ms-tan" />}
            title="Complete your tax setup"
            description="Set your tax country and accounting method to see your holdings"
            action={{ label: "Complete Setup", to: "/quiz" }}
          />
        ) : getExchangeValues(profile?.linkedExchangeAccounts).length ? (
          <EmptyWidget
            icon={<ChartPieIcon className="h-8 w-8" />}
            title="Waiting for your first import"
            description="Your source is connected — holdings land here as soon as the first import finishes"
            action={{ label: "View sources", to: "/integrations" }}
          />
        ) : (
          <EmptyWidget
            icon={<ChartPieIcon className="h-8 w-8" />}
            title="Connect to see your holdings"
            description="Add an exchange or wallet to track your portfolio"
            action={{ label: "Connect Wallet", to: "/settings/exchange/new" }}
          />
        )}
      </section>
    );
  }

  return (
    <section className="flex flex-col">
      <DHead
        label="Holdings"
        right={
          <HeadLink to="/portfolio">
            {positionsWithValue.length} asset{positionsWithValue.length !== 1 ? "s" : ""}
          </HeadLink>
        }
      />

      <div className="flex flex-col">
      {topHoldings.map((position, index) => {
        const percentage = totalValue > 0 ? ((position.currentValue || 0) / totalValue) * 100 : 0;
        const priceChange = position.price_change_24h || 0;

        return (
          <FigLink
            key={`${position.asset}-${position.exchange}-${index}`}
            to={`/portfolio?asset=${encodeURIComponent(position.asset)}`}
            lead={<CoinMark sym={position.asset} />}
            label={position.asset}
            sub={
              <>
                {formatAmount(position.quantity)} {position.asset} · {percentage.toFixed(1)}%
                {position.exchangeName ? ` · ${position.exchangeName}` : ""}
              </>
            }
            tag={
              priceChange !== 0 ? (
                <Tag tone={priceChange > 0 ? "pos" : "warn"}>
                  {priceChange > 0 ? "+" : ""}
                  {priceChange.toFixed(1)}%
                </Tag>
              ) : undefined
            }
            value={formatCurrency(position.currentValue || 0)}
          />
        );
      })}
      </div>
    </section>
  );
};
